import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { participantsAPI } from '../lib/api';
import { ArrowLeft, AlertTriangle, Wallet, Target, Pill, Phone, MapPin } from 'lucide-react';

export default function ParticipantDetail() {
  const { participantId } = useParams();
  const [p, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);

  const money = (v) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

  useEffect(() => {
    participantsAPI.getById(participantId)
      .then(res => setParticipant(res.data))
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return <div className="p-6 text-slate-500">Loading participant...</div>;
  if (!p) return <div className="p-6 text-slate-500">Participant not found.</div>;

  const usedPct = p.funding_total > 0 ? Math.round((p.funding_used / p.funding_total) * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      <Link to="/participants" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft size={16} /> Back to Participants
      </Link>

      <div className="rounded-2xl border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{p.full_name}</h1>
            <p className="text-slate-500">NDIS: {p.ndis_number || 'Not provided'}</p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-600">
              {p.phone && <span className="inline-flex items-center gap-1"><Phone size={15} /> {p.phone}</span>}
              {p.address && <span className="inline-flex items-center gap-1"><MapPin size={15} /> {p.address}</span>}
            </div>
          </div>
          <span className={`rounded-full px-3 py-1 text-sm font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            {p.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <Wallet className="mb-3 text-blue-500" />
          <div className="text-2xl font-bold">{money(p.funding_total)}</div>
          <div className="text-sm text-slate-500">Funding Total</div>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <Wallet className="mb-3 text-purple-500" />
          <div className="text-2xl font-bold">{money(p.funding_remaining)}</div>
          <div className="text-sm text-slate-500">Remaining</div>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <Target className="mb-3 text-emerald-500" />
          <div className="text-2xl font-bold">{p.goals?.length || 0}</div>
          <div className="text-sm text-slate-500">Goals</div>
        </div>
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <AlertTriangle className="mb-3 text-amber-500" />
          <div className="text-2xl font-bold">{p.risk_flags?.length || 0}</div>
          <div className="text-sm text-slate-500">Risk Flags</div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-bold">Funding Utilisation</h2>
        <div className="mb-2 flex justify-between text-sm text-slate-600">
          <span>{money(p.funding_used)} used</span>
          <span>{usedPct}%</span>
        </div>
        <div className="h-3 rounded-full bg-slate-100">
          <div className="h-3 rounded-full bg-slate-900" style={{ width: `${Math.min(usedPct, 100)}%` }} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold">Goals</h2>
          {p.goals?.length ? p.goals.map((g, i) => (
            <div key={i} className="mb-2 rounded-xl bg-emerald-50 p-3 text-emerald-800">{g}</div>
          )) : <p className="text-slate-500">No goals recorded.</p>}
        </section>

        <section className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold">Risk Flags</h2>
          {p.risk_flags?.length ? p.risk_flags.map((r, i) => (
            <div key={i} className="mb-2 rounded-xl bg-amber-50 p-3 text-amber-800">{r}</div>
          )) : <p className="text-slate-500">No risk flags recorded.</p>}
        </section>

        <section className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold">Medications</h2>
          {p.medications?.length ? p.medications.map((m, i) => (
            <div key={i} className="mb-2 flex items-center gap-2 rounded-xl bg-blue-50 p-3 text-blue-800">
              <Pill size={16} /> {m}
            </div>
          )) : <p className="text-slate-500">No medications recorded.</p>}
        </section>

        <section className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold">Notes</h2>
          <p className="whitespace-pre-wrap text-slate-700">{p.notes || 'No notes recorded.'}</p>
        </section>
      </div>
    </div>
  );
}
